'use client';

import { useCallback, useState } from 'react';
import type { UseFormRegisterReturn } from 'react-hook-form';

import { cn } from '@/lib/utils';

interface WebsiteDisclosureFieldProps {
  /** Unique input id — also targeted to focus the field on reveal. */
  id: string;
  /** Toggle text and the input's accessible name. */
  label?: string;
  /** Result of `register('website')` from an RHF host form. */
  registration?: UseFormRegisterReturn;
  /** Plain `name` for non-RHF (FormData) host forms. Ignored when `registration` is set. */
  name?: string;
  /** Validation message, if any. */
  error?: string;
  /** Called when the field is collapsed — clear the RHF value here so a
   *  hidden field never submits stale input. */
  onClear?: () => void;
  /** Analytics focus hook, fired on the input's first focus. */
  onFocusField?: () => void;
  /** Classes for the <input>, matched to the host form's other fields. */
  inputClassName: string;
  /** Classes (mainly text color) for the toggle button. */
  toggleClassName?: string;
  /** Classes for the error message. */
  errorClassName?: string;
  /** `data-analytics-location` value for the toggle. */
  analyticsLocation?: string;
}

const DEFAULT_LABEL = 'Add your website';

/**
 * Optional "Website" field hidden behind a subtle toggle ("tab"). Clicking the
 * toggle smoothly reveals the input: the row height animates via the
 * grid-rows-[0fr→1fr] trick (no height measuring) while the inner content
 * fades + slides in, delayed on open so it appears as the row finishes
 * expanding. Collapsing clears the value through `onClear`. All motion is
 * disabled under `prefers-reduced-motion`.
 *
 * The input stays mounted in both states so React Hook Form retains its value
 * across toggles; an empty value is harmless (optional on both client and
 * server, and filtered out of the lead email).
 */
export function WebsiteDisclosureField({
  id,
  label = DEFAULT_LABEL,
  registration,
  name,
  error,
  onClear,
  onFocusField,
  inputClassName,
  toggleClassName,
  errorClassName,
  analyticsLocation,
}: WebsiteDisclosureFieldProps) {
  const [open, setOpen] = useState(false);
  const errorId = `${id}-error`;

  const toggle = useCallback(() => {
    if (open) {
      onClear?.();
      // Plain (non-RHF) forms: clear the DOM input so a collapsed field never
      // submits a stale value.
      if (!registration) {
        const el = document.getElementById(id);
        if (el instanceof HTMLInputElement) el.value = '';
      }
      setOpen(false);
    } else {
      setOpen(true);
      // Focus the field once revealed for a keyboard-friendly flow.
      requestAnimationFrame(() => {
        document.getElementById(id)?.focus();
      });
    }
  }, [open, id, onClear, registration]);

  return (
    <div className="flex flex-col">
      <button
        type="button"
        onClick={toggle}
        aria-expanded={open}
        aria-controls={id}
        data-analytics-cta="website_toggle"
        data-analytics-location={analyticsLocation}
        className={cn(
          'group flex w-fit items-center gap-2 text-[14px] font-light uppercase tracking-[-0.02em] transition-colors hover:text-accent lg:text-[16px]',
          toggleClassName ?? 'text-[#666]',
        )}
      >
        <span className="flex size-5 items-center justify-center rounded-full border border-current transition-colors group-hover:border-accent">
          <svg
            width="12"
            height="12"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth={2}
            aria-hidden="true"
            className={cn(
              'transition-transform duration-500 ease-[cubic-bezier(0.16,1,0.3,1)]',
              open && 'rotate-45',
            )}
          >
            <path d="M12 5v14M5 12h14" />
          </svg>
        </span>
        {label}
      </button>

      <div
        className={cn(
          'grid transition-[grid-template-rows] duration-[450ms] ease-[cubic-bezier(0.16,1,0.3,1)] motion-reduce:transition-none',
          open ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]',
        )}
      >
        <div className="overflow-hidden">
          <div
            className={cn(
              'pt-4 transition-[opacity,transform] duration-300 ease-out motion-reduce:transition-none',
              open ? 'translate-y-0 opacity-100 delay-150' : '-translate-y-1 opacity-0',
            )}
          >
            <input
              id={id}
              type="url"
              inputMode="url"
              autoComplete="url"
              placeholder="https://yourstore.com"
              aria-label={label}
              tabIndex={open ? undefined : -1}
              aria-hidden={open ? undefined : true}
              aria-invalid={error ? 'true' : undefined}
              aria-describedby={error ? errorId : undefined}
              className={inputClassName}
              {...(registration ?? (name ? { name } : {}))}
              onFocus={onFocusField}
            />
            {error && (
              <p id={errorId} className={errorClassName ?? 'mt-2 text-[14px] text-red-500'}>
                {error}
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
